import React, { useEffect, useRef } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import {
  Badge,
  Button,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Select, Input, MenuItem, Checkbox, ListItemText
} from "@material-ui/core";
import ClearIcon from '@material-ui/icons/Clear';
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import RefreshIcon from "@material-ui/icons/Refresh";
import FormControl from "@material-ui/core/FormControl";
import { Column, Summary, TotalItem, Editing, RangeRule } from "devextreme-react/data-grid";
import { Template } from "devextreme-react/core/template";
import { exportDataGrid } from 'devextreme/excel_exporter';
import 'devextreme/data/odata/store';
import { DateTimePicker } from "@material-ui/pickers";
import ExcelJS from 'exceljs';
import saveAs from 'file-saver';
import { format } from "date-fns";
import DataGrid from 'components/devExtreme/DataGrid';
import { Constants } from "../../utilities/constants";
import {
  LoadingOverlayWrapper,
  ButtonDropdown,
  ButtonPopover
} from "../layout";
import {
  fetchInventoryData,
  setSelectedStrategies,
  setAllStrategiesIncluded,
  setStrategiesOpen,
  setTbbpThreshold,
  setFromTime,
  setToTime,
  fetchRegions,
  fetchStrategies,
  setDashboardMaxPriceByRegion
} from "../../store/actions";
import {sendClientLog} from 'services/logService';
import { CheckTaskCompleted, IsSubmitted } from "services/taskManagementService";

const exportItems = [
  { text: 'Target Inventory', value: 'targetInventory' },
  { text: 'Supplemental Inventory', value: 'supplementalInventory' },
  { text: 'All', value: 'all' },
];

const Inventory = ({
  classes,
  inventoryType,
  rentalInventory,
  retailInventory,
  fetchInventoryData,
  setFromTime,
  setToTime,
  setSelectedStrategies,
  setStrategiesOpen,
  setAllStrategiesIncluded,
  setDashboardMaxPriceByRegion,
  setTbbpThreshold,
  dashboardRegions,
  dashboardStrategies,
  fetchRegions,
  fetchStrategies,
}) => {
  const isRental = inventoryType.toLowerCase() === 'rental';
  const percentOfBidPrice = isRental
    ? 'targetBidPriceRentalPercentOfPrice'
    : 'targetBidPriceRetailPercentOfPrice';
  const descriptiveFitType = isRental
    ? 'descriptiveRentalFit'
    : 'descriptiveRetailFit';

  const inventoryStore = isRental ? rentalInventory : retailInventory;
  const {
    isLoading,
    inventoryData,
    fromDate,
    toDate,
    fromTime,
    toTime,
    selectedStrategies,
    strategiesOpen,
    allStrategiesIncluded,
    tbbpThreshold,
    dashboardMaxPrices,
  } = inventoryStore;

  const today = new Date();
  const maxImportDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  const minImportDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() -30);

  const targetInventoryGridRef = useRef(null);
  const supplementalInventoryGridRef = useRef(null);
  const [totalTbppThresholdPercent, setTotalTbppThresholdPercent] = React.useState(0);
  const [totalBlankTBPPPercent, setTotalBlankTBPPPercent] = React.useState(0);
  const [totalExpectationPercent, setTotalExpectationPercent] = React.useState(0);
  const [totalSupplementalPercent, setTotalSupplementalPercent] = React.useState(0);
  const [dataLoaded, setDataLoaded] = React.useState(false);

  const [targetData, setTargetData] = React.useState([]);
  const [supplementalData, setSupplementalData] = React.useState([]);

  const inventoryChannels = [
    Constants.Channel.MLS,
    Constants.Channel.OFFMARKET,
  ];

  const maxAskingPriceValue = 9999999.0;

  useEffect(() => {
    refreshData();

    if(!dataLoaded) { // prevents loop call when fetchRegions / fetchStrategies returns 0 results
      // fetch regions and strategies if they haven't fetched yet
      !dashboardRegions.length && fetchRegions();
      !dashboardStrategies.length && fetchStrategies();
      setDataLoaded(true);
    }

    sendClientLog('Info',`User opened inventory tab in Dashboard (${inventoryType})`);
  }, [dashboardRegions]);

  useEffect(() => {
    prepareTargetData(inventoryData, tbbpThreshold);
    prepareSupplementalData(inventoryData, tbbpThreshold);
  }, [inventoryData, tbbpThreshold, dashboardMaxPrices]);

  function refreshData() {
    fetchInventoryData(inventoryType);
  }

  function onRowUpdated(e) {
    const data = { region: e.data.region, maxPrice: e.data.maxPrice, inventoryType: inventoryType };
    setDashboardMaxPriceByRegion(data);
  }

  function prepareTargetData(inventoryData, tbbpThresholdValue) {
    const groupedOpportunities = groupByRegion(
      inventoryData.filter(o => inventoryChannels.includes(o.channelText))
    );
    const tbbpFilterPercent = tbbpThresholdValue/100;

    let totalTbppThresholdFiltered = 0;
    let totalCompletedTBPPFiltered = 0;
    let totalBlankTBPP = 0;
    let totalCompletedTBPP = 0;
    let totalExpectation = 0;
    let totalCompleted = 0;

    const data = dashboardRegions.map(region => {
      const regionMaxPrice = dashboardMaxPrices[region] || maxAskingPriceValue;
      const regionDataRaw = groupedOpportunities[region] || [];
      const regionData = regionDataRaw.filter(o => o.askingPrice <= regionMaxPrice);

      const tbppThresholdFiltered = [];
      const tbpp = [];
      const buyBoxFit = [];

      regionData.forEach((regionItem) => {
        if(regionItem[descriptiveFitType] === true) {
          if (regionItem[percentOfBidPrice] === null) {
            tbpp.push(regionItem);
          } else if (regionItem[percentOfBidPrice] >= tbbpFilterPercent) {
            tbppThresholdFiltered.push(regionItem);
          }

          if (regionItem['descriptiveFit'] === true) {
            buyBoxFit.push(regionItem);
          }
        }
      });

      const buyBoxFitLength = buyBoxFit.length;
      const tbppThresholdFilteredLength = tbppThresholdFiltered.length;
      const tbppLength = tbpp.length;

      const completedTBPPFiltered = tbppThresholdFiltered.filter(
        o => CheckTaskCompleted(o)
      ).length;

      const completedTBPP = tbpp.filter(o => CheckTaskCompleted(o)).length;

      const completedTBPPFilteredPercent = tbppThresholdFilteredLength > 0
        ? (+(completedTBPPFiltered / tbppThresholdFilteredLength).toFixed(2))
        : 0;
      const submittedTBPPFiltered = tbppThresholdFiltered.filter(
        o => IsSubmitted(o.underwriteStatus)
      ).length;
      const completedTBPPPercent = tbppLength > 0
        ? (+(completedTBPP / tbppLength).toFixed(2))
        : 0;
      const submittedTBPP = tbpp.filter(o => IsSubmitted(o.underwriteStatus)).length;
      const expectation = tbppThresholdFilteredLength + tbppLength;
      const completed = completedTBPPFiltered + completedTBPP;
      const completedPercent = expectation > 0
        ? (+(completed / expectation).toFixed(2))
        : 0;

      totalTbppThresholdFiltered += tbppThresholdFilteredLength;
      totalCompletedTBPPFiltered += completedTBPPFiltered;
      totalBlankTBPP += tbppLength;
      totalCompletedTBPP += completedTBPP;
      totalExpectation += expectation;
      totalCompleted += completed;

      return {
        region,
        inventory: regionData.length,
        buyBoxFit: buyBoxFitLength,
        tbppThresholdFiltered: tbppThresholdFilteredLength,
        completedTBPPFiltered: completedTBPPFiltered,
        completedTBPPFilteredPercent: completedTBPPFilteredPercent,
        submittedTBPPFiltered: submittedTBPPFiltered,
        blankTBPP: tbppLength,
        completedTBPP: completedTBPP,
        completedTBPPPercent: completedTBPPPercent,
        submittedTBPP: submittedTBPP,
        expectation: expectation,
        completed: completed,
        completedPercent: completedPercent,
        maxPrice: regionMaxPrice,
      };
    });

    const totalTbppThresholdPercent = totalCompletedTBPPFiltered/totalTbppThresholdFiltered || 0;
    const totalBlankTBPPPercent = totalCompletedTBPP/totalBlankTBPP || 0;
    const totalExpectationPercent = totalCompleted/totalExpectation || 0;

    setTotalTbppThresholdPercent(totalTbppThresholdPercent);
    setTotalBlankTBPPPercent(totalBlankTBPPPercent);
    setTotalExpectationPercent(totalExpectationPercent);
    setTargetData(data);

    return data;
  }

  function prepareSupplementalData(inventoryData, tbbpThresholdValue) {
    const groupedOpportunities = groupByRegion(
      inventoryData.filter(o => inventoryChannels.includes(o.channelText))
    );
    const tbbpFilterPercent = tbbpThresholdValue/100;

    let totalTbppThresholdFiltered = 0;
    let totalCompletedTBPPFiltered = 0;

    const data = dashboardRegions.map(region => {
      const regionData = groupedOpportunities[region] || [];
      const tbppFiltered = regionData.filter(
        o => o[percentOfBidPrice] < tbbpFilterPercent && o[percentOfBidPrice] !== null && o[descriptiveFitType] === true
      );
      const completedTBPPFiltered = tbppFiltered.filter(o => CheckTaskCompleted(o)).length;

      const tbppFilteredLength = tbppFiltered.length;
      const completedTBPPFilteredPercent = tbppFilteredLength > 0
        ? (+(completedTBPPFiltered / tbppFilteredLength).toFixed(2))
        : 0;
      const submittedTBPPFiltered = tbppFiltered.filter(o => IsSubmitted(o.underwriteStatus)).length;

      totalTbppThresholdFiltered += tbppFilteredLength;
      totalCompletedTBPPFiltered += completedTBPPFiltered;

      return {
        region,
        tbppThresholdFiltered: tbppFilteredLength,
        completedTBPPFiltered: completedTBPPFiltered,
        completedTBPPFilteredPercent: completedTBPPFilteredPercent,
        submittedTBPPFiltered: submittedTBPPFiltered,
      };
    });

    setTotalSupplementalPercent(totalCompletedTBPPFiltered/totalTbppThresholdFiltered || 0);
    setSupplementalData(data);
  }

  function groupByRegion(inventoryData) {
    const grouped = {};
    inventoryData.forEach(o => {
      const region = o.region || 'N/A';
      if (grouped[region] === undefined) {
        grouped[region] = [];
      }

      grouped[region].push(o);
    });

    return grouped;
  }

  function importStartTimeChanged(e) {
    setFromTime(e, inventoryType);
  }

  function imporEndTimeChanged(e) {
    setToTime(e, inventoryType);
  }

  const handleStrategiesOpen = () => {
    setStrategiesOpen(true, inventoryType);
  };

  const handleStrategiesClose = () => {
    setStrategiesOpen(false, inventoryType);
  };

  const strategiesChanged = (e) => {
    const newStrategies = e.target.value;
    setSelectedStrategies(newStrategies, inventoryType);
    fetchInventoryData(inventoryType);
  }

  const getStrategiesSelector = () => {
    const dataSource = ["Blank"].concat(dashboardStrategies);
    return (
      <Select
        multiple
        value={selectedStrategies}
        onChange={strategiesChanged}
        input={<Input />}
        style={{ width: '100%', marginBottom: 4 }}
        renderValue={(selected) => selected.join(', ')}
        MenuProps={{ 
          style: { maxHeight: 600 },
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
        }}
        disabled={allStrategiesIncluded !== 'contains'}
      >
        {dataSource.map((strategy) => (
          <MenuItem key={strategy} value={strategy} dense>
            <Checkbox checked={selectedStrategies.indexOf(strategy) > -1} />
            <ListItemText primary={strategy} />
          </MenuItem>
        ))}
      </Select>
    );
  }

  const allStrategiesIncludedChanged = (e) => {
    const value = e.target.value;
    setAllStrategiesIncluded(value, inventoryType);
    fetchInventoryData(inventoryType);
  }

  const tbbpThresholdChanged = (e) => {
    const value = e.target.value;
    setTbbpThreshold(value, inventoryType);

    prepareTargetData(inventoryData, value);
  }

  function onTargetGridToolbarPreparing(e) {
    e.toolbarOptions.items.unshift(
      {
        location: 'before',
        template: 'targetGridHeaderRender'
      }
    );
  }

  function targetGridToolbarItemRender() {
    return (
      <div className="informer">
        <Typography variant="h6">Target Inventory</Typography>
      </div>
    );
  }

  function handleExcelExport(e) {
    switch (e.value) {
      case 'targetInventory':
        targetInventoryGridRef
        && onExporting(targetInventoryGridRef, getTargetInventoryExportName());

        break;
      case 'supplementalInventory':
        supplementalInventoryGridRef
        && onExporting(supplementalInventoryGridRef, getSupplementalInventoryExportName());

        break;
      case 'all':
        if (targetInventoryGridRef && supplementalInventoryGridRef) {
          exportAllGrids();
        }

        break;
      default: break;
    }

    sendClientLog('Info', 'User exported inventory data to excel (' + e.value + ')');
  }

  function getTimestamp() {
    return format(new Date(), '_yyyy-MM-dd_HHmmss');
  }

  function getTargetInventoryExportName() {
    return `ReVUe_TargetInventory_Export_${getTimestamp()}`;
  }

  function getSupplementalInventoryExportName() {
    return `ReVUe_SupplementalInventory_Export_${getTimestamp()}`;
  }

  function onExporting(ref, name) {
    const workbook = new ExcelJS.Workbook(); 
    const worksheet = workbook.addWorksheet('Sheet');

    exportDataGrid({
        component: ref.current.instance,
        worksheet: worksheet
    }).then(function() {
        workbook.xlsx.writeBuffer().then(function(buffer) {
            saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${name}.xlsx`);
        });
    });
  }

  function exportAllGrids() {
    const workbook = new ExcelJS.Workbook();
    const targetInventory = workbook.addWorksheet('Target Inventory');
    const supplementalInventory = workbook.addWorksheet('Supplemental Inventory');
    exportDataGrid({
      worksheet: targetInventory,
      component: targetInventoryGridRef.current.instance,
    }).then(() => {
      return exportDataGrid({
        worksheet: supplementalInventory,
        component: supplementalInventoryGridRef.current.instance,
      });
    }).then(() => {
      workbook.xlsx.writeBuffer()
        .then((buffer) => {
          saveAs(
            new Blob([buffer], { type: 'application/octet-stream' }),
            `ReVUe_Inventories_Export_${ getTimestamp() }.xlsx`);
        });
    });
  }

  function customizeTotalPercentFormat(data) {
    if (data.value === 0) return data.value;
    return (+(data.value * 100).toFixed(2)) + '%';
  }

  function calculateTargetCustomSummary(options) {
    switch (options.name) {
      case 'completedTBPPFilteredPercent':
        switch(options.summaryProcess) {
          default:
            options.totalValue = totalTbppThresholdPercent;
        }
        break;
      case 'completedTBPPPercent':
        switch(options.summaryProcess) {
          default:
            options.totalValue = totalBlankTBPPPercent;
        }
        break;
      case 'completedPercent':
        switch(options.summaryProcess) {
          default:
            options.totalValue = totalExpectationPercent;
        }
        break;
    }
  }

  function calculateSupplementCustomSummary(options) {
    switch (options.name) {
      case 'completedTBPPFilteredPercent':
        switch(options.summaryProcess) {
          default:
            options.totalValue = totalSupplementalPercent;
        }
        break;
    }
  }

  // const [dateSelectionError, setDateSelectionError] = React.useState(null);
  // useEffect for catching dateSelection form Errors
  useEffect(() => {
    if (fromDate?.toLocaleDateString() === toDate?.toLocaleDateString() && fromTime.getTime() > toTime.getTime()
    ) {
      // setDateSelectionError("Start time cannot be earlier than end time");
    } else {
      // setDateSelectionError(null);
      fetchInventoryData(inventoryType);
    }
  }, [fromTime, fromDate, toDate, toTime]);

  const columnHeaderClass = `${classes.mainHeader} ${classes.columnBorder}`
  const amountDisplayFormat = { style: 'currency', currency: 'USD', minimumSignificantDigits: 1 };
  const amountEditorFormat = { type: 'fixedPoint', precision: 0 };

  return (
    <LoadingOverlayWrapper
      active={isLoading}
      message={isLoading ? 'Fetching data...' : ''}
      className={classes.loadingOverlayWrapper}
      background={'rgba(242, 242, 242, 0.9)'}
    >
      <div id='strategiesSelectorDiv'></div>
      <Grid container spacing={1}>
        <Grid item xs={12}>
        <style jsx="true">{`
          .e-daterange-hidden {
            display: none;
          }
          .e-dlg-ref-element {
            display: inline;
          }
          div.e-dlg-container {
            position: fixed !important;
          }
          .dx-datagrid .dx-row {
            height: 20px;
          }
          .dx-datagrid .dx-row td {
            font-size: 11px;
            padding: 1px;
          }
          .e-time-wrapper {
            max-width: 95px !important;
          }
          .e-date-range-wrapper {
            width: 190px !important;
            margin: 5px 20px;
          }
          .e-daterangepicker {
            margin-left: 116px;
            margin-top: 1px;
          }
          .e-datetime-wrapper {
            margin: 0px 10px;
          }
        `}</style>
          <ButtonPopover title='IMPORTED ON' popoverStyle={{ width: 440, height: 120, padding: 10, display: "inline-flex", flexWrap: "wrap" }} >
            <DateTimePicker
              renderInput={(props) => <TextField variant="outlined" {...props} />}
              label="Start Time"
              value={fromTime}
              onChange={importStartTimeChanged}
              minDate={minImportDate}
              maxDate={maxImportDate}
            />
            <DateTimePicker
              style={{ marginLeft: 16 }}
              renderInput={(props) => <TextField variant="outlined" {...props} />}
              label="End Time"
              value={toTime}
              onChange={imporEndTimeChanged}
              minDate={minImportDate}
              maxDate={maxImportDate}
            />
          </ButtonPopover>

          <Button
            className={classes.buttonWithIcon}
            variant={'contained'}
            color={'primary'}
            style={{ color: 'white' }}
            onClick={handleStrategiesOpen}
            id='strategiesSelectorButton'
          >
            STRATEGIES
            <ArrowDropDownIcon className={classes.buttonRightIcon} />
          </Button>

          <ButtonDropdown
            className={classes.exportButton}
            title='Export To Excel'
            onSelect={handleExcelExport}
            items={exportItems}
            buttonWidth={175}
          />
          <Dialog
            open={strategiesOpen}
            onClose={handleStrategiesClose}
            maxWidth='xs'
            fullWidth
            classes={{ paper: classes.dialogContainer }}
          >
            <DialogTitle>Strategies</DialogTitle>
            <IconButton className={classes.closeButton} onClick={handleStrategiesClose}>
              <ClearIcon fontSize="inherit" />
            </IconButton>
            <DialogContent>
              <FormControl component="fieldset">
                <RadioGroup aria-label="" name="allStrategiesIncluded" value={allStrategiesIncluded} onChange={allStrategiesIncludedChanged}>
                  <FormControlLabel value="includeAll" control={<Radio />} label="All" />
                  <FormControlLabel value="contains" control={<Radio />} label="Contains.." />
                </RadioGroup>
              </FormControl>
              {getStrategiesSelector()}
            </DialogContent>
          </Dialog>

          <FormControl variant="outlined">
            <TextField
              label="TBPP THRESHOLD"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{
                min: 0,
                style: { textAlign: 'right' }
              }}
              type="number"
              value={tbbpThreshold}
              onChange={tbbpThresholdChanged}
            />
          </FormControl>

          <Tooltip title='Refresh Data'>
            <Badge
              classes={{ colorPrimary: classes.colorPrimary, badge: classes.refreshBadge }}
              color='primary'
              overlap='rectangular'
            >
              <IconButton onClick={() => {refreshData();}} className={classes.refreshButton}>
                <RefreshIcon />
              </IconButton>
            </Badge>
          </Tooltip>

        </Grid>

        <Grid item xs={9}>
          <DataGrid
            ref={targetInventoryGridRef}
            id={ isRental
              ? 'dashboardRentalTargetInventoryGrid'
              : 'dashboardRetailTargetInventoryGrid'
            }
            persistState
            dataSource={targetData}
            allowColumnReordering={true}
            showBorders={true}
            paging={{enabled: false}}
            onToolbarPreparing={onTargetGridToolbarPreparing}
            showColumnLines={true}
            onRowUpdated={onRowUpdated}
          >
            <Editing allowUpdating={true} mode="cell" /> {/* 'row' | 'batch' | 'cell' | 'form' | 'popup' */}
            <Column caption="Inventory Summary" cssClass={columnHeaderClass}>
              <Column dataField='region' caption='Region' width={120} allowEditing={false}/>
              <Column dataField='maxPrice' caption='Max Price' format={amountDisplayFormat} editorOptions={{format: amountEditorFormat, showClearButton: true}}>
                <RangeRule message={`Max price must be between 0 and ${maxAskingPriceValue}`} max={maxAskingPriceValue} min={0} />
              </Column>
              <Column dataField='inventory' caption='Inventory' format="fixedPoint" allowEditing={false}/>
              <Column dataField='buyBoxFit' caption='Buy Box Fit' format="fixedPoint" cssClass={classes.columnBorder} allowEditing={false} />
            </Column>

            <Column
              caption={`${inventoryType} TBPP >= ${tbbpThreshold}%`}
              cssClass={columnHeaderClass}
            >
              <Column
                dataField='tbppThresholdFiltered'
                caption="Count"
                format="fixedPoint"
                allowEditing={false}
              />
              <Column
                dataField='completedTBPPFiltered'
                caption='Completed'
                format="fixedPoint"
                allowEditing={false}
              />
              <Column
                dataField='completedTBPPFilteredPercent'
                caption='% Complete'
                format="percent"
                allowEditing={false}
              />
              <Column
                dataField='submittedTBPPFiltered'
                caption='Submitted Pricing'
                format="fixedPoint"
                cssClass={classes.columnBorder}
                allowEditing={false}
              />
            </Column>

            <Column caption={`${inventoryType} TBPP Blank`} cssClass={columnHeaderClass}>
              <Column
                dataField='blankTBPP'
                caption="Count"
                format="fixedPoint"
                allowEditing={false}
              />
              <Column
                dataField='completedTBPP'
                caption='Completed'
                format="fixedPoint"
                allowEditing={false}
              />
              <Column
                dataField='completedTBPPPercent'
                caption='% Complete'
                format="percent"
                allowEditing={false}
              />
              <Column
                dataField='submittedTBPP'
                caption='Submitted Pricing'
                format="fixedPoint"
                cssClass={classes.columnBorder}
                allowEditing={false}
              />
            </Column>

            <Column
              caption="Pricing Expectation"
              cssClass={`${classes.mainHeader} ${classes.pricingExpectation}`}>
              <Column
                dataField='expectation'
                caption='Expectation'
                format="fixedPoint"
                cssClass={classes.pricingExpectation}
                allowEditing={false}
              />
              <Column
                dataField='completed'
                caption='Completed'
                format="fixedPoint"
                cssClass={classes.pricingExpectation}
                allowEditing={false}
              />
              <Column
                dataField='completedPercent'
                caption='% Complete'
                format="percent"
                cssClass={classes.pricingExpectation}
                allowEditing={false}
              />
            </Column>

            <Summary calculateCustomSummary={calculateTargetCustomSummary}>
              <TotalItem showInColumn="region" displayFormat="Total: "/>
              <TotalItem
                column="inventory"
                summaryType="sum"
                displayFormat="{0}"
                valueFormat="fixedPoint"
              />
              <TotalItem
                column="buyBoxFit"
                summaryType="sum"
                displayFormat="{0}"
                valueFormat="fixedPoint"
              />
              <TotalItem
                column="tbppThresholdFiltered"
                summaryType="sum"
                displayFormat="{0}"
                valueFormat="fixedPoint"
              />
              <TotalItem
                column="completedTBPPFiltered"
                summaryType="sum"
                displayFormat="{0}"
                valueFormat="fixedPoint"
              />
              <TotalItem
                name="completedTBPPFilteredPercent"
                column="completedTBPPFilteredPercent"
                summaryType="custom"
                displayFormat="{0}"
                customizeText={customizeTotalPercentFormat}
              />
              <TotalItem
                column="submittedTBPPFiltered"
                summaryType="sum"
                displayFormat="{0}"
                valueFormat="fixedPoint"
              />
              <TotalItem
                column="blankTBPP"
                summaryType="sum"
                displayFormat="{0}"
                valueFormat="fixedPoint"
              />
              <TotalItem
                column="completedTBPP"
                summaryType="sum"
                displayFormat="{0}"
                valueFormat="fixedPoint"
              />
              <TotalItem
                name="completedTBPPPercent"
                column="completedTBPPPercent"
                summaryType="custom"
                displayFormat="{0}"
                customizeText={customizeTotalPercentFormat}
              />
              <TotalItem
                column="submittedTBPP"
                summaryType="sum"
                displayFormat="{0}"
                valueFormat="fixedPoint"
              />
              <TotalItem
                column="expectation"
                summaryType="sum"
                displayFormat="{0}"
                valueFormat="fixedPoint"
                cssClass={classes.pricingExpectation}
              />
              <TotalItem
                column="completed"
                summaryType="sum"
                displayFormat="{0}"
                valueFormat="fixedPoint"
                cssClass={classes.pricingExpectation}
              />
              <TotalItem
                name="completedPercent"
                column="completedPercent"
                summaryType="custom"
                displayFormat="{0}"
                customizeText={customizeTotalPercentFormat}
                cssClass={classes.pricingExpectation}
              />
            </Summary>
            <Template name="targetGridHeaderRender" render={targetGridToolbarItemRender} />
          </DataGrid>
        </Grid>

        <Grid item xs={3} className={classes.supplementalInventoryGrid}>
          <Typography variant="h6" className={classes.supplementalInventoryHeader}>Supplemental Inventory</Typography>
          <DataGrid
            ref={supplementalInventoryGridRef}
            id={ isRental
              ? 'dashboardRentalSupplementalInventoryGrid'
              : 'dashboardRetailSupplementalInventoryGrid'
            }
            persistState
            dataSource={supplementalData}
            allowColumnReordering={true}
            showBorders={true}
            paging={{enabled: false}}
            showColumnLines={true}
          >
            <Column caption={`${inventoryType} TBPP < ${tbbpThreshold}%`} cssClass={classes.mainHeader}>
              <Column dataField='region' caption='Region' width={120} />
              <Column
                dataField='tbppThresholdFiltered'
                caption="Count"
                format="fixedPoint"
              />
              <Column
                dataField='completedTBPPFiltered'
                caption='Completed'
                format="fixedPoint"
              />
              <Column
                dataField='completedTBPPFilteredPercent'
                caption='% Complete'
                format="percent"
              />
              <Column
                dataField='submittedTBPPFiltered'
                caption='Submitted Pricing'
                format="fixedPoint"
              />
            </Column>

            <Summary calculateCustomSummary={calculateSupplementCustomSummary}>
              <TotalItem showInColumn="region" displayFormat="Total: "/>
              <TotalItem
                column="tbppThresholdFiltered"
                summaryType="sum"
                displayFormat="{0}"
                valueFormat="fixedPoint"
              />
              <TotalItem
                column="completedTBPPFiltered"
                summaryType="sum"
                displayFormat="{0}"
                valueFormat="fixedPoint"
              />
              <TotalItem
                name="completedTBPPFilteredPercent"
                column="completedTBPPFilteredPercent"
                summaryType="custom"
                displayFormat="{0}"
                customizeText={customizeTotalPercentFormat}
              />
              <TotalItem
                column="submittedTBPPFiltered"
                summaryType="sum"
                displayFormat="{0}"
                valueFormat="fixedPoint"
              />
            </Summary>
          </DataGrid>
        </Grid>
      </Grid>

    </LoadingOverlayWrapper>
  );
};

const styles = theme => ({
  buttonWithIcon: {
    margin: theme.spacing(1)
  },
  exportButton: {
    display: 'inline',
  },
  supplementalInventoryGrid: {
    marginTop: 14,
  },
  supplementalInventoryHeader: {
    marginBottom: 10,
  },
  mainHeader: {
    textAlign: 'center !important',
  },
  pricingExpectation: {
    backgroundColor: '#80a4c7',
    color: 'white'
  },
  columnBorder: {
    borderRight: '4px solid rgb(224, 224, 224) !important'
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    backgroundColor: '#ffffff'
}
});

const mapStateToProps = (state) => ({
  rentalInventory: state.dashboardState.rentalInventory,
  retailInventory: state.dashboardState.retailInventory,
  dashboardRegions: state.dashboardState.dashboardRegions,
  dashboardStrategies: state.dashboardState.dashboardStrategies,
});

const mapDispatchToProps = (dispatch) => ({
  fetchInventoryData: (inventoryType) => dispatch(fetchInventoryData({inventoryType})),
  setFromTime: (date, inventoryType) => dispatch(setFromTime({date, inventoryType})),
  setToTime: (date, inventoryType) => dispatch(setToTime({date, inventoryType})),
  setSelectedStrategies: (strategies, inventoryType) => dispatch(setSelectedStrategies({strategies, inventoryType})),
  setStrategiesOpen: (state, inventoryType) => dispatch(setStrategiesOpen({state, inventoryType})),
  setAllStrategiesIncluded: (state, inventoryType) => dispatch(setAllStrategiesIncluded({state, inventoryType})),
  setTbbpThreshold: (level, inventoryType) => dispatch(setTbbpThreshold({level, inventoryType})),
  setDashboardMaxPriceByRegion: (regionData) => dispatch(setDashboardMaxPriceByRegion(regionData)),
  fetchRegions: () => dispatch(fetchRegions({})),
  fetchStrategies: () => dispatch(fetchStrategies({})),
});

export default compose(
  withStyles(styles),
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(Inventory);
